import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { IndosbookLogo } from "./IndosbookLogo";
import { contentPages } from "../mock";
import { Button } from "./ui/button";
import {
  ArrowLeft,
  Users,
  ShieldCheck,
  Newspaper,
  Search,
  Briefcase,
  Building2,
  BookOpen,
  GraduationCap,
  Handshake,
  LifeBuoy,
  BadgeCheck,
  AlertTriangle,
  Mail,
  Check,
  ArrowUpRight,
} from "lucide-react";
import { BackToTop } from "./BackToTop";
import { usePageMeta } from "../lib/seo";

const iconMap = {
  Users,
  ShieldCheck,
  Newspaper,
  Search,
  Briefcase,
  Building2,
  BookOpen,
  GraduationCap,
  Handshake,
  LifeBuoy,
  BadgeCheck,
  AlertTriangle,
};

const ContentPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const page = contentPages[slug];
  const [active, setActive] = useState(null);
  const [progress, setProgress] = useState(0);

  // track which section is under the sticky chip bar
  useEffect(() => {
    if (!page) return undefined;
    const onScroll = () => {
      let current = null;
      for (const sec of page.sections) {
        const el = document.getElementById(sec.id);
        if (el && el.getBoundingClientRect().top <= 160) current = sec.id;
      }
      const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(max > 0 ? Math.min(1, window.scrollY / max) : 0);
      setActive(atBottom ? page.sections[page.sections.length - 1].id : current);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [page]);

  // always open the page from the very top
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  usePageMeta({
    title: page?.seoTitle || (page ? `${page.eyebrow} | Indosbook` : "Page not found | Indosbook"),
    description: page?.seoDescription || page?.subtitle || "",
    path: `/pages/${slug}`,
    noindex: !page,
  });

  // in-page chips scroll smoothly to their section, below the sticky header
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (!page) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-white">
        <p className="text-lg font-semibold text-gray-700">Page not found.</p>
        <Link to="/" className="font-bold text-[#FF4500] hover:underline">
          Back to home
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* top bar */}
      <header className="sticky top-0 z-40 border-b border-gray-100 bg-white/90 backdrop-blur-md">
        <div className="mx-auto flex h-14 max-w-[1100px] items-center justify-between px-4 sm:px-6">
          <Link to="/">
            <IndosbookLogo size={28} />
          </Link>
          <Link
            to="/"
            className="flex items-center gap-1.5 text-sm font-semibold text-gray-600 transition-colors hover:text-[#FF4500]"
          >
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
        </div>
        <div className="absolute inset-x-0 -bottom-px h-[3px]" data-testid="reading-progress-track">
          <div
            className="h-full origin-left bg-[#FF4500] transition-transform duration-150 ease-out"
            style={{ transform: `scaleX(${progress})` }}
            data-testid="reading-progress-bar"
          />
        </div>
      </header>

      <BackToTop />

      {/* hero */}
      <section className="indos-hero-mesh indos-grid-pattern relative overflow-hidden border-b border-stone-200/80">
        <div className="relative mx-auto max-w-[1100px] px-4 sm:px-6 py-14 md:py-20">
          <p className="text-sm font-bold uppercase tracking-wider text-[#FF4500]">
            {page.eyebrow}
          </p>
          <h1 className="font-heading mt-2 max-w-3xl text-4xl sm:text-5xl font-extrabold leading-[1.08] text-gray-900">
            {page.title}
          </h1>
          <p className="mt-4 max-w-2xl text-lg leading-relaxed text-gray-600">
            {page.subtitle}
          </p>
        </div>
      </section>

      {/* sticky section chips */}
      <nav className="relative z-30 sm:sticky sm:top-14 border-b border-stone-200/80 bg-white/85 backdrop-blur-md" data-testid="content-section-nav">
        <div className="mx-auto flex max-w-[1100px] flex-col gap-2.5 px-4 py-4 sm:flex-row sm:overflow-x-auto sm:py-3 sm:px-6 [scrollbar-width:none]" data-testid="content-section-chips">
          {page.sections.map((s, idx) => {
            const ChipIcon = iconMap[s.icon] || Users;
            const on = active === s.id;
            return (
              <button
                key={s.id}
                onClick={() => scrollTo(s.id)}
                aria-current={on ? "true" : undefined}
                data-testid={`section-chip-${s.id}`}
                className={`group flex w-full shrink-0 items-center sm:inline-flex sm:w-auto gap-2.5 rounded-full border py-1.5 pl-1.5 pr-4 text-sm font-semibold shadow-sm transition-[transform,border-color,background-color,box-shadow] duration-200 hover:-translate-y-0.5 ${on ? "border-[#0B2D8C] bg-[#0B2D8C] shadow-md shadow-[#0B2D8C]/25" : "border-[#0B2D8C]/15 bg-white shadow-[#0B2D8C]/5 hover:border-[#FF4500]/50 hover:bg-orange-50/60 hover:shadow-md hover:shadow-orange-500/10"}`}
              >
                <span className={`flex h-7 w-7 items-center justify-center rounded-full transition-colors duration-200 ${on ? "bg-[#FF4500] text-white" : "bg-[#0B2D8C]/[0.07] text-[#0B2D8C] group-hover:bg-[#FF4500] group-hover:text-white"}`}>
                  <ChipIcon className="h-3.5 w-3.5" />
                </span>
                <span className={`text-[11px] font-bold ${on ? "text-orange-300" : "text-[#FF4500]"}`}>{String(idx + 1).padStart(2, "0")}</span>
                <span className={`transition-colors ${on ? "text-white" : "text-slate-800 group-hover:text-[#FF4500]"}`}>{s.heading}</span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* sections */}
      <main className="mx-auto max-w-[1100px] px-4 sm:px-6 py-12 space-y-16">
        {page.sections.map((s, idx) => {
          const Icon = iconMap[s.icon] || Users;
          return (
            <section key={s.id} id={s.id} className="scroll-mt-36">
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#FF4500]/10">
                  <Icon className="h-5 w-5 text-[#FF4500]" />
                </span>
                <span className="text-xs font-bold uppercase tracking-wider text-gray-400">
                  {String(idx + 1).padStart(2, "0")}
                </span>
              </div>
              <h2 className="mt-4 text-3xl font-extrabold text-gray-900">
                {s.heading}
              </h2>
              <p className="mt-3 max-w-2xl text-[15px] leading-relaxed text-gray-600">
                {s.intro}
              </p>

              {/* contact email card */}
              {s.contactEmail && (
                <a
                  href={`mailto:${s.contactEmail}`}
                  className="group mt-6 inline-flex items-center gap-4 rounded-2xl border border-orange-100 bg-orange-50/60 px-5 py-4 transition-all hover:border-[#FF4500] hover:shadow-sm"
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#FF4500]">
                    <Mail className="h-5 w-5 text-white" />
                  </span>
                  <span>
                    <span className="block text-xs font-semibold uppercase tracking-wide text-gray-400">
                      {s.contactNote || "Email us"}
                    </span>
                    <span className="block font-bold text-[#FF4500] group-hover:underline">
                      {s.contactEmail}
                    </span>
                  </span>
                </a>
              )}

              {/* cards */}
              {s.cards && (
                <div className="mt-7 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {s.cards.map((c) => (
                    <div
                      key={c.title}
                      className="rounded-2xl border border-gray-100 bg-gray-50/70 p-6 transition-all hover:-translate-y-1 hover:bg-white hover:shadow-md"
                    >
                      <h3 className="font-bold text-gray-900">{c.title}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-gray-600">
                        {c.desc}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {/* numbered list (guidelines) */}
              {s.list && (
                <ol className="mt-7 space-y-3">
                  {s.list.map((item, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-4 rounded-xl border border-gray-100 bg-white p-4"
                    >
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#FF4500] text-sm font-bold text-white">
                        {i + 1}
                      </span>
                      <p className="pt-0.5 text-[15px] leading-relaxed text-gray-700">
                        {item}
                      </p>
                    </li>
                  ))}
                </ol>
              )}

              {/* feature checklist */}
              {s.features && (
                <ul className="mt-7 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {s.features.map((f) => (
                    <li key={f} className="flex items-start gap-3">
                      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#FF4500]/10">
                        <Check className="h-3.5 w-3.5 text-[#FF4500]" />
                      </span>
                      <span className="text-[15px] leading-relaxed text-gray-700">
                        {f}
                      </span>
                    </li>
                  ))}
                </ul>
              )}

              {/* feed posts */}
              {s.posts && (
                <div className="mt-7 grid grid-cols-1 gap-5 md:grid-cols-3">
                  {s.posts.map((p) => (
                    <article
                      key={p.title}
                      className="group flex flex-col rounded-2xl border border-gray-100 bg-white p-6 transition-all hover:-translate-y-1 hover:shadow-md"
                    >
                      <div className="flex items-center gap-2 text-xs">
                        <span className="rounded-full bg-orange-50 px-2.5 py-1 font-semibold text-[#FF4500]">
                          {p.tag}
                        </span>
                        <span className="text-gray-400">{p.date}</span>
                      </div>
                      <h3 className="mt-4 font-bold leading-snug text-gray-900 group-hover:text-[#FF4500]">
                        {p.title}
                      </h3>
                      <p className="mt-2 flex-1 text-sm leading-relaxed text-gray-600">
                        {p.excerpt}
                      </p>
                      <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#FF4500]">
                        Read more <ArrowUpRight className="h-4 w-4" />
                      </span>
                    </article>
                  ))}
                </div>
              )}
            </section>
          );
        })}

        {/* CTA */}
        <section className="indos-grid-white relative overflow-hidden rounded-3xl bg-[#FF4500] px-8 py-12 text-center sm:px-16">
          <h2 className="font-heading relative text-3xl font-extrabold text-white">
            Ready to be part of the community?
          </h2>
          <p className="relative mx-auto mt-3 max-w-xl text-white/90">
            Create your free account and join seafarers, companies and institutes
            already sailing with Indosbook.
          </p>
          <Button
            onClick={() => navigate("/signup")}
            className="relative mt-7 h-12 rounded-full bg-white px-8 text-base font-bold text-[#FF4500] hover:bg-orange-50"
          >
            Get started free
          </Button>
        </section>
      </main>

      <footer className="border-t border-gray-100 bg-gray-50 py-8 text-center text-sm text-gray-400">
        © 2026 Indosbook. Built for seafarers, companies and maritime institutes.
      </footer>
    </div>
  );
};

export default ContentPage;
